// Taxonomy versioning UI: the version bar, the destructive-edit dialog, and the
// duplicate-as-migration preview.
//
// One idea runs through all three: the admin decides what an edit means, and the interface
// never decides for them. Editing does not silently create a version, and a destructive edit
// does not proceed until the cost is stated in campaigns rather than in fields.

import { useState, useEffect, useCallback } from 'react';
import { txApi } from '../txApi.js';
import { C, btn, btnSm, panel, label, inp, Badge, Err, Spinner, useConfirm } from './_shared.jsx';

const scrim = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};
const dialog = {
  background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`,
  width: '100%', maxWidth: 620, maxHeight: '82vh', display: 'flex', flexDirection: 'column',
};
const dHead = { padding: '16px 20px', borderBottom: `1px solid ${C.borderSoft}` };
const dBody = { padding: '16px 20px', overflowY: 'auto' };
const dFoot = {
  padding: '12px 20px', borderTop: `1px solid ${C.borderSoft}`,
  display: 'flex', justifyContent: 'flex-end', gap: 8,
};

export const STALE_MESSAGE =
  'This taxonomy was updated since you opened it. Reload before editing.';

/** A 409 from any version-aware write means someone else moved first. */
export function isStale(e) {
  return /updated since you opened it/i.test(e?.message || '');
}

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

// ── Version bar ───────────────────────────────────────────────────────────────────────

/**
 * Sits above the taxonomy editor. Shows which version is being edited, lets the admin move
 * through history read-only, and carries the two explicit actions: create a version, or
 * restore an old one.
 *
 * `onVersionChange` hands back the selected version so the editor can lock itself when the
 * admin is looking at an archived one. Archived versions are viewable, never editable —
 * the campaigns built on them are historical records.
 */
export function VersionBar({ clientId, platformId, onVersionChange, refreshKey }) {
  const [versions, setVersions] = useState([]);
  const [selId, setSelId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmUI, ask] = useConfirm();

  const load = useCallback(async () => {
    if (!clientId || !platformId) { setVersions([]); return; }
    setLoading(true); setErr(null);
    try {
      const d = await txApi.listTaxonomyVersions(clientId, platformId);
      setVersions(d.versions || []);
      // Follow the active version unless the admin has deliberately selected another.
      setSelId(prev => {
        const stillThere = (d.versions || []).some(v => v.id === prev);
        return stillThere ? prev : (d.active?.id || d.versions?.[0]?.id || null);
      });
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [clientId, platformId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const selected = versions.find(v => v.id === selId) || null;
  useEffect(() => { onVersionChange?.(selected); }, [selected, onVersionChange]);

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      await txApi.createTaxonomyVersion({
        client_id: clientId, platform_id: platformId,
        note: note.trim() || null,
        // Held to the version we were shown: issuing a version from a stale view would
        // discard whatever someone else introduced in between.
        expected_version_id: versions.find(v => v.status === 'active')?.id || null,
      });
      setCreating(false); setNote('');
      await load();
    } catch (e) { setErr(isStale(e) ? STALE_MESSAGE : e.message); }
    finally { setBusy(false); }
  };

  const restore = async (v) => {
    const okay = await ask({
      title: `Restore v${v.version_num}?`,
      body: `Its configuration is copied into a new version at the top of the sequence and ` +
            `made active. v${v.version_num} itself stays archived.`,
      confirmLabel: 'Restore as new version',
    });
    if (!okay) return;
    setBusy(true); setErr(null);
    try { await txApi.restoreTaxonomyVersion(v.id); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!clientId || !platformId) return null;

  return (
    <div style={panel({ padding: 12, marginBottom: 14 })}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={label({ margin: 0 })}>Taxonomy version</span>

        {loading && <Spinner />}

        <select
          style={inp({ width: 'auto', minWidth: 260 })}
          value={selId || ''}
          onChange={e => setSelId(e.target.value)}
        >
          {versions.map(v => (
            <option key={v.id} value={v.id}>
              v{v.version_num}{v.status === 'active' ? ' · active' : ' · archived'}
              {` · ${v.campaign_count} campaign${v.campaign_count === 1 ? '' : 's'}`}
            </option>
          ))}
          {versions.length === 0 && <option value="">No versions yet</option>}
        </select>

        {selected && (
          <Badge color={selected.status === 'active' ? C.success : C.muted}>
            {selected.status === 'active' ? 'Editing' : 'Read-only'}
          </Badge>
        )}

        <div style={{ flex: 1 }} />

        {selected?.status === 'archived' && (
          <button style={btnSm()} disabled={busy} onClick={() => restore(selected)}>
            Restore as new version
          </button>
        )}
        <button style={btnSm('primary')} disabled={busy || !versions.length}
                onClick={() => setCreating(v => !v)}>
          {creating ? 'Cancel' : 'Create new version'}
        </button>
      </div>

      {selected && (
        <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>
          {selected.field_count} field{selected.field_count === 1 ? '' : 's'} across{' '}
          {selected.level_count} level{selected.level_count === 1 ? '' : 's'} ·{' '}
          created {fmtDate(selected.created_at)}
          {selected.created_by ? ` by ${selected.created_by}` : ''}
          {selected.note ? ` · ${selected.note}` : ''}
        </div>
      )}

      {selected?.status === 'archived' && (
        <div style={{
          marginTop: 8, fontSize: 12, color: C.warn, background: C.brassWash,
          border: `1px solid ${C.brass}33`, borderRadius: 8, padding: '8px 10px',
        }}>
          <b>This version is archived.</b> Its {selected.campaign_count} campaign
          {selected.campaign_count === 1 ? '' : 's'} are historical records — viewable, not
          editable. Restore it to make changes, which creates a new version at the top.
        </div>
      )}

      {creating && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={label()}>What changed? (optional)</label>
            <input style={inp()} value={note} maxLength={200}
                   placeholder="e.g. Added Objective, dropped Quarter"
                   onChange={e => setNote(e.target.value)} />
          </div>
          <button style={btn('primary')} disabled={busy} onClick={create}>
            {busy ? 'Creating…' : 'Create version'}
          </button>
        </div>
      )}
      {creating && (
        <p style={{ fontSize: 12, color: C.muted, marginTop: 8, marginBottom: 0 }}>
          The current configuration is copied into a new version, which becomes active.
          Existing campaigns stay on the version they were built under and keep rendering
          as they do now.
        </p>
      )}

      <Err msg={err} />
      {confirmUI}
    </div>
  );
}

// ── Destructive in-place edit ─────────────────────────────────────────────────────────

/**
 * Shown before an in-place edit that destroys data. Counts campaigns, not fields, because
 * "removing Market affects 3 fields" is not a consequence anyone can weigh.
 *
 * The confirm button stays disabled until the impact has actually loaded — a dialog that
 * can be dismissed before it knows the cost is worse than no dialog.
 */
export function ImpactDialog({ versionId, fields, action, onCancel, onConfirm }) {
  const [impact, setImpact] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    txApi.versionImpact(versionId, fields.map(f => f.id))
      .then(d => { if (alive) setImpact(d); })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [versionId, fields]);

  const total = impact?.total_affected_campaigns ?? null;
  const names = fields.map(f => f.name).join(', ');

  return (
    <div style={scrim} onClick={onCancel}>
      <div style={dialog} onClick={e => e.stopPropagation()}>
        <div style={dHead}>
          <h3 style={{ margin: 0, fontSize: 16, fontFamily: C.display }}>
            {action === 'move' ? 'Move' : 'Remove'} {names}?
          </h3>
        </div>
        <div style={dBody}>
          {!impact && !err && <Spinner />}
          {err && <Err msg={err} />}

          {impact && total === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: C.dim }}>
              No campaign holds a value for {names}, so nothing is lost. This edits the
              active version in place — no new version is created.
            </p>
          )}

          {impact && total > 0 && (
            <>
              <p style={{
                margin: '0 0 12px', fontSize: 13, color: C.danger, fontWeight: 600,
              }}>
                {action === 'move'
                  ? `Moving ${names} will permanently clear its value from ${total} existing campaign${total === 1 ? '' : 's'}.`
                  : `Removing ${names} will permanently clear the value from ${total} existing campaign${total === 1 ? '' : 's'}.`}
                {' '}This cannot be undone.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: C.muted }}>
                    <th style={{ padding: '6px 8px' }}>Field</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Values lost</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Campaigns on field</th>
                  </tr>
                </thead>
                <tbody>
                  {impact.fields.map(f => (
                    <tr key={f.field_id} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                      <td style={{ padding: '6px 8px' }}>{f.name}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right',
                                   fontFamily: C.mono,
                                   color: f.affected_campaigns ? C.danger : C.muted }}>
                        {f.affected_campaigns}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right',
                                   fontFamily: C.mono, color: C.muted }}>
                        {f.campaigns_on_field}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 12, color: C.muted, marginTop: 12, marginBottom: 0 }}>
                To keep these campaigns as they are, create a new version instead — they stay
                on the version they were built under.
              </p>
            </>
          )}
        </div>
        <div style={dFoot}>
          <button style={btn()} onClick={onCancel}>Cancel</button>
          <button
            style={btn(total > 0 ? 'danger' : 'primary')}
            disabled={!impact || busy}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}
          >
            {busy ? 'Applying…'
              : total > 0 ? `Yes, ${action === 'move' ? 'move' : 'remove'} and clear ${total} value${total === 1 ? '' : 's'}`
              : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Duplicate as migration ────────────────────────────────────────────────────────────

const RowList = ({ items, tone, render }) => (
  <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, color: tone }}>
    {items.map((it, i) => <li key={i} style={{ marginBottom: 2 }}>{render(it)}</li>)}
  </ul>
);

/**
 * The same preview, for duplicating a whole campaign group from the campaigns list.
 *
 * `DuplicateDialog` migrates one saved row and commits it server-side. This one covers every
 * row of a group and commits nothing: it hands the mapping back so the caller can open the
 * builder pre-filled, which is where a group duplicate has always landed.
 *
 * Grouped by channel, then level, because that is how the mapping is computed — a channel
 * carries its own taxonomy version, and two channels can name a level the same thing while
 * defining different fields under it.
 *
 * Row counts carry the weight here. Which fields match is a taxonomy fact the admin may
 * already know; how many rows lose a value to a field the new version dropped is not.
 */
export function GroupDuplicateDialog({ group, onCancel, onConfirm, utmSummary }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    // Every channel, not just the group's primary one: a campaign's rows live under each
    // channel it uses, and a preview of one of them would understate what the copy carries.
    txApi.groupDuplicatePreview(group.id)
      .then(d => { if (alive) setPlan(d); })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [group.id]);

  const disabled = new Set((group.disabled_platform_ids || []).map(String));
  const channels = plan?.channels || [];
  const levels = channels.flatMap(ch => ch.levels);
  const lostRows = levels.reduce(
    (n, lv) => n + lv.discarded.reduce((m, d) => m + d.filled_rows, 0), 0);
  const nothingCarries = plan && levels.every(lv => lv.matched.length === 0);
  const skippedRows = channels
    .filter(ch => disabled.has(String(ch.platform_id)))
    .reduce((n, ch) => n + ch.row_count, 0)
    + levels.filter(lv => lv.unmapped_level).reduce((n, lv) => n + lv.row_count, 0);

  return (
    <div className="o-scrim" onClick={onCancel}>
      <div className="o-modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="o-modal-head">
          <h2>Duplicate {group.name}</h2>
          {plan && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted }}>
              {plan.counts.rows} row{plan.counts.rows === 1 ? '' : 's'} across{' '}
              {plan.counts.channels} channel{plan.counts.channels === 1 ? '' : 's'} · fields
              are matched by name, per channel
            </p>
          )}
        </div>

        <div className="o-modal-body" style={{ overflowY: 'auto' }}>
          {utmSummary}
          {!plan && !err && <Spinner />}
          {err && <Err msg={err} />}

          {plan && plan.counts.rows === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: C.dim }}>
              This campaign has no rows yet, so the copy starts empty.
            </p>
          )}

          {plan && nothingCarries && plan.counts.rows > 0 && (
            <div className="o-hint" style={{ marginBottom: 14 }}>
              <b>No field names match.</b> The current taxonomy renamed or replaced every
              field this campaign was built on, so the copy starts empty.
            </div>
          )}

          {channels.map(ch => {
            const off = disabled.has(String(ch.platform_id));
            return (
            <div key={ch.platform_id} style={{ marginBottom: 20 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10,
                paddingBottom: 6, borderBottom: `1px solid ${C.borderSoft}`,
              }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                  {ch.platform_name || 'Channel'}
                </span>
                <span style={{ fontSize: 12, color: C.muted }}>
                  {ch.row_count} row{ch.row_count === 1 ? '' : 's'} ·{' '}
                  {ch.levels.length} level{ch.levels.length === 1 ? '' : 's'}
                </span>
                {off && <Badge color={C.danger}>Channel disabled</Badge>}
              </div>

              {off ? (
                <p style={{ margin: 0, fontSize: 12, color: C.danger }}>
                  This channel is switched off for {group.client_name || 'this client'}, so its
                  rows are not carried over — a copy of them would be read-only from the start.
                </p>
              ) : ch.levels.map(lv => (
            <section key={`${ch.platform_id}-${lv.level}`} style={{ marginBottom: 18 }}>
              <div style={label({ margin: '0 0 6px' })}>
                {lv.level} · {lv.row_count} row{lv.row_count === 1 ? '' : 's'}
                {lv.source_version && lv.target_version
                  && ` · v${lv.source_version.version_num} → v${lv.target_version.version_num}`}
              </div>

              {lv.unmapped_level ? (
                <p style={{ margin: 0, fontSize: 12, color: C.danger }}>
                  These rows cannot be carried over — {lv.unmapped_reason}.
                </p>
              ) : (
                <table className="o-tbl">
                  <thead>
                    <tr>
                      <th>Field</th><th>Carries over</th><th className="o-num">Rows with a value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lv.matched.map(m => (
                      <tr key={m.target_field_id}>
                        <td className="o-strong">{m.name}</td>
                        <td>
                          <Badge color={C.success}>Matched</Badge>
                          {m.type_changed && (
                            <span style={{ color: C.warn, fontSize: 11, marginLeft: 6 }}>
                              field type changed — values may not validate
                            </span>
                          )}
                          {m.now_required && (
                            <span style={{ color: C.warn, fontSize: 11, marginLeft: 6 }}>
                              now required
                            </span>
                          )}
                        </td>
                        <td className="o-num">{m.filled_rows}</td>
                      </tr>
                    ))}
                    {lv.discarded.map(d => (
                      <tr key={`d-${d.name}`}>
                        <td className="o-strong">{d.name}</td>
                        <td>
                          <Badge color={C.danger}>Dropped</Badge>
                          <span style={{ color: C.muted, fontSize: 11, marginLeft: 6 }}>
                            not in the current taxonomy
                          </span>
                        </td>
                        <td className="o-num">{d.filled_rows}</td>
                      </tr>
                    ))}
                    {lv.introduced.map(n => (
                      <tr key={n.target_field_id}>
                        <td className="o-strong">{n.name}</td>
                        <td>
                          <Badge color={C.muted}>New</Badge>
                          <span style={{ color: C.muted, fontSize: 11, marginLeft: 6 }}>
                            {n.is_required ? 'required — needs a value' : 'optional — starts empty'}
                          </span>
                        </td>
                        <td className="o-num">0</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
              ))}
            </div>
            );
          })}

          {plan && plan.counts.rows > 0 && (
            <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
              {lostRows > 0
                ? `${lostRows} value${lostRows === 1 ? '' : 's'} sit on dropped fields and will not be copied. `
                : ''}
              {skippedRows > 0
                ? `${skippedRows} row${skippedRows === 1 ? '' : 's'} are not carried at all. `
                : ''}
              The copy opens in the builder as unsaved work — nothing is written until you save
              it, and {group.name} is not changed.
              {plan.counts.channels > 1
                && ' Each channel is filled in as you switch to it, and saved separately.'}
            </p>
          )}
        </div>

        <div className="o-modal-foot">
          <button className="o-btn" onClick={onCancel}>Cancel</button>
          <button className="o-btn o-btn-primary" disabled={!plan || busy}
            onClick={() => { setBusy(true); onConfirm(plan); }}>
            {busy ? 'Copying…' : 'Create with matching data'}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * The preview the spec requires before a campaign is moved onto the current taxonomy.
 *
 * Fields are matched by display name across versions, so the mapping is shown rather than
 * asserted — an admin who renamed a field needs to see that its value is about to be
 * discarded, not discover it afterwards.
 */
export function DuplicateDialog({ campaignId, campaignName, onCancel, onDone }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    txApi.duplicatePreview(campaignId)
      .then(d => { if (alive) setPlan(d); })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [campaignId]);

  const commit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await txApi.duplicateCampaign(campaignId);
      onDone?.(r);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const c = plan?.counts;

  return (
    <div style={scrim} onClick={onCancel}>
      <div style={dialog} onClick={e => e.stopPropagation()}>
        <div style={dHead}>
          <h3 style={{ margin: 0, fontSize: 16, fontFamily: C.display }}>
            Duplicate onto the current taxonomy
          </h3>
          {plan && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted }}>
              {campaignName ? <><b>{campaignName}</b> · </> : null}
              v{plan.source_version?.version_num} → v{plan.target_version?.version_num}
              {' · '}{plan.level} level
            </p>
          )}
        </div>

        <div style={dBody}>
          {!plan && !err && <Spinner />}
          {err && <Err msg={err} />}

          {plan && (
            <>
              <p style={{ margin: '0 0 14px', fontSize: 13 }}>
                <b>{c.carried} field{c.carried === 1 ? '' : 's'} carried over</b>,{' '}
                {c.discarded} discarded,{' '}
                {c.new_required > 0
                  ? `${c.new_required} new field${c.new_required === 1 ? '' : 's'} need values.`
                  : `${c.new} new field${c.new === 1 ? '' : 's'} come through empty.`}
              </p>

              {plan.matched.length > 0 && (
                <section style={{ marginBottom: 14 }}>
                  <div style={label({ margin: 0 })}>Carried over</div>
                  <RowList items={plan.matched} tone={C.text} render={m => (
                    <>
                      <b>{m.name}</b>
                      {m.value ? <> — <span style={{ fontFamily: C.mono }}>{String(m.value)}</span></>
                               : <span style={{ color: C.muted }}> — empty</span>}
                      {m.type_changed && <span style={{ color: C.warn }}> · field type changed, value may not validate</span>}
                      {m.now_required && <span style={{ color: C.warn }}> · now required</span>}
                    </>
                  )} />
                </section>
              )}

              {plan.discarded.length > 0 && (
                <section style={{ marginBottom: 14 }}>
                  <div style={label({ margin: 0 })}>Discarded — not in the new version</div>
                  <RowList items={plan.discarded} tone={C.danger} render={d => (
                    <>
                      <b>{d.name}</b>
                      {d.value ? <> — <span style={{ fontFamily: C.mono }}>{String(d.value)}</span> will be lost</>
                               : <span style={{ color: C.muted }}> — no value to lose</span>}
                    </>
                  )} />
                </section>
              )}

              {plan.introduced.length > 0 && (
                <section>
                  <div style={label({ margin: 0 })}>New — come through empty</div>
                  <RowList items={plan.introduced} tone={C.dim} render={n => (
                    <>
                      <b>{n.name}</b>
                      {n.is_required ? <span style={{ color: C.warn }}> · required</span>
                                     : <span style={{ color: C.muted }}> · optional</span>}
                    </>
                  )} />
                </section>
              )}

              <p style={{ fontSize: 12, color: C.muted, marginTop: 14, marginBottom: 0 }}>
                The duplicate lands as a draft until its required fields are filled. The
                original campaign is not changed and stays on v{plan.source_version?.version_num}.
              </p>
            </>
          )}
        </div>

        <div style={dFoot}>
          <button style={btn()} onClick={onCancel}>Cancel</button>
          <button style={btn('primary')} disabled={!plan || busy} onClick={commit}>
            {busy ? 'Duplicating…' : 'Duplicate as draft'}
          </button>
        </div>
      </div>
    </div>
  );
}
